const TOKEN_KEY = "sec.token";

// Base path: detect from current location (works at / and /Secure_Endpoint/)
const BASE = (() => {
  const p = window.location.pathname;
  const i = p.indexOf("/api/");
  if (i > 0) return p.slice(0, i);
  // SPA route like /devices or /login — take first segment if it's not a known route
  const seg = p.split("/")[1];
  if (seg && !["", "login", "dashboard", "devices", "requests", "groups", "users", "remote"].includes(seg)) {
    return "/" + seg;
  }
  return "";
})();

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export function apiBase(): string { return BASE; }

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

interface Options {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { method = "GET", body, signal } = options;
  const token = getToken();

  let response: Response;
  try {
    response = await fetch(BASE + path, {
      method,
      signal,
      headers: {
        accept: "application/json",
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(0, "network_error", "Falha de conexao com o servidor");
  }

  if (response.status === 401 && token) {
    setToken(null);
    window.dispatchEvent(new CustomEvent("sec:unauthorized"));
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const envelope = payload as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? "error",
      envelope?.error?.message ?? `Erro ${response.status}`,
    );
  }

  return payload as T;
}

export async function apiBlob(path: string): Promise<Blob> {
  const token = getToken();
  let response: Response;
  try {
    response = await fetch(BASE + path, {
      headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) },
    });
  } catch {
    throw new ApiError(0, "network_error", "Falha de conexao com o servidor");
  }
  if (!response.ok) {
    let code = "error";
    let message = `Erro ${response.status}`;
    try {
      const payload = (await response.json()) as { error?: { code?: string; message?: string } };
      code = payload?.error?.code ?? code;
      message = payload?.error?.message ?? message;
    } catch { /* nao-JSON */ }
    throw new ApiError(response.status, code, message);
  }
  return response.blob();
}

export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : "";
}
